/**
 * DesignSystemPage — HRL-DS Documentation vivante
 * Route : /admin/design-system
 * 7 onglets : Fondations / Atoms / Molecules / Organisms / Charts / Patterns / Layouts
 */

import { useState } from 'react';
import styles from './DesignSystemPage.module.css';
import {
  Button, Badge, Input, Textarea, Select,
  Checkbox, Radio, Switch, Spinner, Divider, Avatar,
} from '@/shared/ds/atoms';
import {
  FormGroup, SearchBar, AlertBanner, TagList, ProgressBar,
  Breadcrumb, Pagination, CardHeader,
} from '@/shared/ds/molecules';
import { GlassCard, SectionHeader, Modal, FormCard, DataTable } from '@/shared/ds/organisms';
import type { Column } from '@/shared/ds/organisms';
import { LineChart, BarChart, DonutChart, MiniStats, Gauge } from '@/shared/ds/charts';
import { PatientCard, HitlCard, VoiceRecorder, VoiceStatusPill, FloatingPanel, EpicRow, StoryRow } from '@/shared/ds/patterns';
import type { VoiceRecorderSize, VoiceRecorderState } from '@/shared/ds/patterns/VoiceRecorder/VoiceRecorder';
import { VOICE_STATE_DOCS, VOICE_LABEL, VOICE_COLOR } from '@/shared/ds/patterns/VoiceRecorder/voiceStates';
import type { VoiceStateDoc } from '@/shared/ds/patterns/VoiceRecorder/voiceStates';
import {
  MainLayout, AdminShellLayout, AuthLayout, DashboardGrid,
  TwoColumnLayout, FullPageCenter, CardGrid, TimelineLayout,
} from '@/shared/ds/layouts';
import type { TimelineItem } from '@/shared/ds/layouts';

type TabId = 'fondations' | 'atoms' | 'molecules' | 'organisms' | 'charts' | 'patterns' | 'layouts';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  available: boolean;
}

const TABS: Tab[] = [
  { id: 'fondations', label: 'Fondations', icon: 'bi-layers',        available: true  },
  { id: 'atoms',      label: 'Atoms',      icon: 'bi-circle',        available: true  },
  { id: 'molecules',  label: 'Molecules',  icon: 'bi-diagram-2',     available: true  },
  { id: 'organisms',  label: 'Organisms',  icon: 'bi-grid-3x3',      available: true  },
  { id: 'charts',     label: 'Charts',     icon: 'bi-bar-chart',     available: true  },
  { id: 'patterns',   label: 'Patterns',   icon: 'bi-puzzle',        available: true  },
  { id: 'layouts',    label: 'Layouts',    icon: 'bi-layout-sidebar', available: true  },
];

// ── Swatches couleur ──────────────────────────────────────────────────────────

interface Swatch {
  label: string;
  token: string;
  value: string;
  dark?: boolean;
}

const SWATCHES_VIOLET: Swatch[] = [
  { label: 'Purple',        token: '--hrl-purple',        value: '#9333ea', dark: true  },
  { label: 'Purple Light',  token: '--hrl-purple-light',  value: '#a855f7', dark: true  },
  { label: 'Purple Dark',   token: '--hrl-purple-dark',   value: '#7e22ce', dark: true  },
  { label: 'Purple Darker', token: '--hrl-purple-darker', value: '#6b21a8', dark: true  },
  { label: 'Purple Soft',   token: '--hrl-purple-soft',   value: 'rgba(147,51,234,0.08)', dark: false },
];

const SWATCHES_ACTION: Swatch[] = [
  { label: 'Action',       token: '--hrl-action',       value: '#16a34a', dark: true  },
  { label: 'Action Hover', token: '--hrl-action-hover', value: '#15803d', dark: true  },
  { label: 'Action Light', token: '--hrl-action-light', value: '#dcfce7', dark: false },
];

const SWATCHES_SEMANTIC: Swatch[] = [
  { label: 'Success',      token: '--hrl-success',       value: '#16a34a', dark: true  },
  { label: 'Warning',      token: '--hrl-warning',       value: '#d97706', dark: true  },
  { label: 'Danger',       token: '--hrl-danger',        value: '#dc2626', dark: true  },
  { label: 'Info',         token: '--hrl-info',          value: '#2563eb', dark: true  },
];

const SWATCHES_NEUTRAL: Swatch[] = [
  { label: 'Gray 50',  token: '--hrl-gray-50',  value: '#f9fafb', dark: false },
  { label: 'Gray 100', token: '--hrl-gray-100', value: '#f3f4f6', dark: false },
  { label: 'Gray 200', token: '--hrl-gray-200', value: '#e5e7eb', dark: false },
  { label: 'Gray 300', token: '--hrl-gray-300', value: '#d1d5db', dark: false },
  { label: 'Gray 500', token: '--hrl-gray-500', value: '#6b7280', dark: true  },
  { label: 'Gray 700', token: '--hrl-gray-700', value: '#374151', dark: true  },
  { label: 'Gray 900', token: '--hrl-gray-900', value: '#111827', dark: true  },
];

// ── Composant Swatch ──────────────────────────────────────────────────────────

function ColorSwatch({ swatch }: { swatch: Swatch }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`var(${swatch.token})`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className={styles.swatch} onClick={handleCopy} title={`Copier var(${swatch.token})`}>
      <div
        className={styles.swatchColor}
        style={{ background: swatch.value }}
      >
        {copied && (
          <span className={styles.swatchCopied}>
            <i className="bi bi-check2"></i>
          </span>
        )}
      </div>
      <div className={styles.swatchInfo}>
        <span className={styles.swatchLabel}>{swatch.label}</span>
        <code className={styles.swatchToken}>{swatch.token}</code>
        <span className={styles.swatchValue}>{swatch.value}</span>
      </div>
    </div>
  );
}

// ── Onglet Fondations ─────────────────────────────────────────────────────────

function TabFondations() {
  return (
    <div className={styles.tabContent}>

      {/* Palette Violet */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-circle-fill me-2" style={{ color: 'var(--hrl-purple)' }}></i>
          Violet — Couleur primaire
        </h3>
        <p className={styles.sectionDesc}>Identité Horaliia — navigation, focus, interactions principales.</p>
        <div className={styles.swatchGrid}>
          {SWATCHES_VIOLET.map(s => <ColorSwatch key={s.token} swatch={s} />)}
        </div>
      </section>

      {/* Palette Action */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-circle-fill me-2" style={{ color: 'var(--hrl-action)' }}></i>
          Vert — Couleur action
        </h3>
        <p className={styles.sectionDesc}>Validation, confirmation, actions positives (approuver, enregistrer, valider).</p>
        <div className={styles.swatchGrid}>
          {SWATCHES_ACTION.map(s => <ColorSwatch key={s.token} swatch={s} />)}
        </div>
      </section>

      {/* Couleurs sémantiques */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-palette me-2" style={{ color: 'var(--hrl-purple)' }}></i>
          Sémantique
        </h3>
        <p className={styles.sectionDesc}>Feedback utilisateur — succès, avertissement, erreur, information.</p>
        <div className={styles.swatchGrid}>
          {SWATCHES_SEMANTIC.map(s => <ColorSwatch key={s.token} swatch={s} />)}
        </div>
      </section>

      {/* Neutres */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-circle-half me-2" style={{ color: 'var(--hrl-gray-500)' }}></i>
          Neutres
        </h3>
        <p className={styles.sectionDesc}>Texte, bordures, fonds — échelle gris pour le contenu et les surfaces.</p>
        <div className={styles.swatchGrid}>
          {SWATCHES_NEUTRAL.map(s => <ColorSwatch key={s.token} swatch={s} />)}
        </div>
      </section>

      {/* Glassmorphism */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-water me-2" style={{ color: 'var(--hrl-purple)' }}></i>
          Glassmorphism
        </h3>
        <p className={styles.sectionDesc}>
          Surfaces translucides — fond blanc 75%, blur 12px, bordure violet 12%.
          Token pivot : <code>--hrl-glass-bg</code>.
        </p>
        <div className={styles.glassDemo}>
          <div className={styles.glassCard}>
            <div className={styles.glassCardTitle}>Glass Card</div>
            <div className={styles.glassCardBody}>
              <code>--hrl-glass-bg</code> · <code>--hrl-glass-border</code> · <code>--hrl-glass-blur</code>
            </div>
          </div>
          <div className={styles.glassCardStrong}>
            <div className={styles.glassCardTitle}>Glass Card — Strong</div>
            <div className={styles.glassCardBody}>
              <code>--hrl-glass-strong-bg</code> · fond 88% · bordure 20%
            </div>
          </div>
        </div>
      </section>

      {/* Typographie */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-type me-2" style={{ color: 'var(--hrl-purple)' }}></i>
          Typographie — Inter
        </h3>
        <div className={styles.typoGrid}>
          {([
            { token: '--hrl-text-3xl', size: '1.875rem', label: '3XL — Titre page' },
            { token: '--hrl-text-2xl', size: '1.5rem',   label: '2XL — Titre section' },
            { token: '--hrl-text-xl',  size: '1.25rem',  label: 'XL — Sous-titre' },
            { token: '--hrl-text-lg',  size: '1.125rem', label: 'LG — Label fort' },
            { token: '--hrl-text-base',size: '1rem',     label: 'Base — Corps' },
            { token: '--hrl-text-sm',  size: '0.875rem', label: 'SM — Secondaire' },
            { token: '--hrl-text-xs',  size: '0.75rem',  label: 'XS — Caption' },
          ] as { token: string; size: string; label: string }[]).map(t => (
            <div key={t.token} className={styles.typoRow}>
              <span style={{ fontSize: t.size, fontFamily: 'var(--hrl-font)', lineHeight: 1.3 }}>
                {t.label}
              </span>
              <code className={styles.typoToken}>{t.token} · {t.size}</code>
            </div>
          ))}
        </div>
      </section>

      {/* Espacements */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <i className="bi bi-arrows-expand me-2" style={{ color: 'var(--hrl-purple)' }}></i>
          Espacement
        </h3>
        <div className={styles.spacingGrid}>
          {([1,2,3,4,5,6,8,10,12,16] as const).map(n => (
            <div key={n} className={styles.spacingRow}>
              <div
                className={styles.spacingBar}
                style={{ width: `calc(var(--hrl-space-${n}) * 4)`, background: 'var(--hrl-purple-soft)', border: '1px solid var(--hrl-purple)' }}
              ></div>
              <code className={styles.spacingToken}>--hrl-space-{n}</code>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

// ── Onglet Atoms ─────────────────────────────────────────────────────────────

function AtomSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hrl-space-3)', alignItems: 'center' }}>
        {children}
      </div>
    </div>
  );
}

function TabAtoms() {
  const [sw1, setSw1] = useState(false);
  const [sw2, setSw2] = useState(true);
  const [cb, setCb] = useState(false);
  const [radio, setRadio] = useState('a');
  const [loading, setLoading] = useState(false);

  const handleLoadingDemo = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1800);
  };

  return (
    <div className={styles.tabContent}>

      {/* Button */}
      <AtomSection title="Button — 5 variants · 3 tailles · loading / disabled">
        <Button variant="primary" size="sm">Primary SM</Button>
        <Button variant="primary">Primary MD</Button>
        <Button variant="primary" size="lg">Primary LG</Button>
        <Button variant="success">Valider</Button>
        <Button variant="danger">Supprimer</Button>
        <Button variant="outline">Outline</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="primary" loading={loading} onClick={handleLoadingDemo}>
          {loading ? 'Chargement…' : 'Démo loading'}
        </Button>
        <Button variant="primary" disabled>Disabled</Button>
      </AtomSection>

      {/* Badge */}
      <AtomSection title="Badge — 6 variants">
        <Badge variant="primary">Primary</Badge>
        <Badge variant="success">Validé</Badge>
        <Badge variant="danger">Erreur</Badge>
        <Badge variant="warning">Attention</Badge>
        <Badge variant="info">Info</Badge>
        <Badge variant="neutral">Neutre</Badge>
      </AtomSection>

      {/* Input */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Input — états default / error / disabled</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 'var(--hrl-space-4)' }}>
          <Input label="Observation" placeholder="Saisir une observation..." />
          <Input label="Avec erreur" placeholder="Champ requis" error="Ce champ est obligatoire" />
          <Input label="Désactivé" value="Valeur figée" disabled />
          <Input label="Recherche" type="search" placeholder="Rechercher un patient..." />
        </div>
      </div>

      {/* Textarea */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Textarea</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--hrl-space-4)' }}>
          <Textarea label="Compte-rendu" placeholder="Saisir le compte-rendu..." rows={3} />
          <Textarea label="Avec erreur" placeholder="Champ requis" error="Minimum 10 caractères" rows={3} />
        </div>
      </div>

      {/* Select */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Select</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--hrl-space-4)' }}>
          <Select
            label="Type d'acte"
            placeholder="Choisir..."
            options={[
              { value: 'med', label: 'Traçabilité médicamenteuse' },
              { value: 'dar', label: 'Transmission DAR' },
              { value: 'obs', label: 'Observation libre' },
            ]}
          />
          <Select label="Désactivé" options={[{ value: 'a', label: 'Option A' }]} disabled />
        </div>
      </div>

      {/* Checkbox + Radio + Switch */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Checkbox · Radio · Switch</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hrl-space-8)', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hrl-space-2)' }}>
            <Checkbox label="Option A" checked={cb} onChange={setCb} id="cb-demo" />
            <Checkbox label="Option B (désactivée)" disabled id="cb-dis" />
          </div>
          <Radio
            name="demo-radio"
            value={radio}
            onChange={setRadio}
            options={[
              { value: 'a', label: 'Assisté' },
              { value: 'b', label: 'Semi-auto' },
              { value: 'c', label: 'Auto-supervisé' },
            ]}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hrl-space-3)' }}>
            <Switch label="Mode autonomie" checked={sw1} onChange={setSw1} />
            <Switch label="Actif par défaut" checked={sw2} onChange={setSw2} />
            <Switch label="Désactivé" disabled checked={false} />
          </div>
        </div>
      </div>

      {/* Spinner + Divider + Avatar */}
      <AtomSection title="Spinner — 3 tailles · 3 variants">
        <Spinner size="sm" label="SM" />
        <Spinner size="md" label="MD" />
        <Spinner size="lg" label="LG" />
        <Spinner variant="action" label="Action" />
        <Spinner variant="neutral" label="Neutral" />
      </AtomSection>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Divider — horizontal · avec label</h3>
        <Divider />
        <div style={{ padding: 'var(--hrl-space-2) 0' }}>
          <Divider label="Section" />
        </div>
      </div>

      <AtomSection title="Avatar — initiales · tailles · statuts">
        <Avatar name="Jean Dupont" size="sm" />
        <Avatar name="Marie Curie" size="md" status="online" />
        <Avatar name="Paul Martin" size="lg" status="away" />
        <Avatar name="Sophie Bernard" status="offline" />
      </AtomSection>

    </div>
  );
}

// ── Onglet Molecules ──────────────────────────────────────────────────────────

function TabMolecules() {
  const [page, setPage] = useState(1);
  const [tags, setTags] = useState(['React', 'FastAPI', 'Podman']);
  return (
    <div className={styles.tabContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>FormGroup + SearchBar</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: 'var(--hrl-space-4)' }}>
          <FormGroup label="Nom du patient" required htmlFor="fg-nom">
            <Input id="fg-nom" placeholder="Saisir le nom..." />
          </FormGroup>
          <FormGroup label="Type d'acte" htmlFor="fg-type" hint="Classifié par IA">
            <Select id="fg-type" options={[{ value: 'med', label: 'Médicamenteux' }, { value: 'obs', label: 'Observation' }]} />
          </FormGroup>
          <FormGroup label="Avec erreur" htmlFor="fg-err" error="Champ obligatoire non renseigné">
            <Input id="fg-err" placeholder="Requis..." error="Champ obligatoire" />
          </FormGroup>
        </div>
        <div style={{ marginTop: 'var(--hrl-space-4)', maxWidth: '320px' }}>
          <SearchBar placeholder="Rechercher un patient..." />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>AlertBanner — 4 niveaux</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hrl-space-3)' }}>
          <AlertBanner variant="info" title="Information">Synchronisation en cours avec le DPI.</AlertBanner>
          <AlertBanner variant="success" title="Validé" dismissible>Observation enregistrée avec succès.</AlertBanner>
          <AlertBanner variant="warning" title="Attention" dismissible>Niveau d'autonomie : Semi-auto — supervision recommandée.</AlertBanner>
          <AlertBanner variant="danger" title="Erreur">Connexion AI Engine perdue. Vérifier le pod.</AlertBanner>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>TagList + ProgressBar + Breadcrumb</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hrl-space-4)' }}>
          <TagList tags={tags} onRemove={t => setTags(prev => prev.filter(x => x !== t))} />
          <ProgressBar value={68} label="Progression E-8 DS-HORALIIA" showPercent />
          <ProgressBar value={38} variant="action" label="Jobs HITL résolus" showPercent />
          <Breadcrumb items={[
            { label: 'Admin' },
            { label: 'Patients' },
            { label: 'Dupont Jean', href: '#' },
            { label: 'Séjour actif' },
          ]} />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>CardHeader + Pagination</h3>
        <CardHeader
          title="Liste des patients"
          subtitle="Service de cardiologie"
          count={42}
          actions={<Button variant="success" size="sm">+ Nouveau</Button>}
        />
        <div style={{ marginTop: 'var(--hrl-space-4)' }}>
          <Pagination currentPage={page} totalPages={7} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}

// ── Onglet Organisms ──────────────────────────────────────────────────────────

interface DemoRow extends Record<string, unknown> {
  id: string; name: string; service: string; statut: string;
}

const DEMO_DATA: DemoRow[] = [
  { id: '1', name: 'Dupont Jean',   service: 'Cardiologie', statut: 'Actif'  },
  { id: '2', name: 'Martin Marie',  service: 'Neurologie',  statut: 'Actif'  },
  { id: '3', name: 'Bernard Paul',  service: 'Urgences',    statut: 'Sorti'  },
  { id: '4', name: 'Leroy Sophie',  service: 'Cardiologie', statut: 'Actif'  },
  { id: '5', name: 'Moreau Luc',    service: 'Chirurgie',   statut: 'Sorti'  },
];

const DEMO_COLS: Column<DemoRow>[] = [
  { key: 'name',    label: 'Nom patient', sortable: true },
  { key: 'service', label: 'Service',     sortable: true },
  { key: 'statut',  label: 'Statut',      render: row => <Badge variant={row.statut === 'Actif' ? 'success' : 'neutral'}>{String(row.statut)}</Badge> },
];

function TabOrganisms() {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div className={styles.tabContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>GlassCard — composant pivot</h3>
        <div style={{ display: 'flex', gap: 'var(--hrl-space-4)', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '220px' }}>
            <GlassCard>
              <p style={{ margin: 0, color: 'var(--hrl-gray-700)', fontSize: 'var(--hrl-text-sm)' }}>
                <strong>GlassCard</strong> — fond blanc 75% + blur 12px + bordure violet 12%
              </p>
            </GlassCard>
          </div>
          <div style={{ minWidth: '220px' }}>
            <GlassCard strong>
              <p style={{ margin: 0, color: 'var(--hrl-gray-700)', fontSize: 'var(--hrl-text-sm)' }}>
                <strong>GlassCard strong</strong> — fond 88% + bordure 20%
              </p>
            </GlassCard>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <SectionHeader
          title="SectionHeader"
          subtitle="Titre de section avec séparateur violet dégradé"
          actions={<Button variant="primary" size="sm">Action CTA</Button>}
        />
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Modal</h3>
        <Button variant="outline" onClick={() => setModalOpen(true)}>Ouvrir Modal</Button>
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Exemple Modal"
          footer={
            <>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Annuler</Button>
              <Button variant="success" onClick={() => setModalOpen(false)}>Confirmer</Button>
            </>
          }
        >
          <p style={{ color: 'var(--hrl-gray-700)', fontSize: 'var(--hrl-text-sm)' }}>
            Contenu du modal — confirmation d'action, formulaire critique, etc.
          </p>
          <div style={{ marginTop: 'var(--hrl-space-3)' }}>
            <AlertBanner variant="info">Utilisé pour les confirmations HITL et les formulaires d'exception.</AlertBanner>
          </div>
        </Modal>
      </div>

      <div className={styles.section}>
        <FormCard
          title="FormCard"
          subtitle="GlassCard + CardHeader + form layout"
          footer={
            <>
              <Button variant="outline">Annuler</Button>
              <Button variant="success" type="submit">Enregistrer</Button>
            </>
          }
        >
          <Input label="Nom" placeholder="Saisir..." />
          <Select label="Service" options={[{ value: 'cardio', label: 'Cardiologie' }, { value: 'neuro', label: 'Neurologie' }]} />
        </FormCard>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>DataTable — tri, pagination, actions</h3>
        <DataTable
          columns={DEMO_COLS}
          data={DEMO_DATA}
          rowKey="id"
          pageSize={3}
          actions={() => <Button variant="ghost" size="sm">Voir</Button>}
        />
      </div>
    </div>
  );
}

// ── Onglet Charts ─────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'];
const JOBS_DATA = [12, 19, 8, 24, 17, 31];
const HITL_DATA = [3, 5, 2, 8, 4, 6];

function TabCharts() {
  return (
    <div className={styles.tabContent}>
      {/* MiniStats */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>MiniStats — KPI cards</h3>
        <div style={{ display: 'flex', gap: 'var(--hrl-space-4)', flexWrap: 'wrap' }}>
          <MiniStats label="Jobs exécutés" value="1 247" delta={12} deltaLabel="ce mois" icon="bi-robot" variant="primary" />
          <MiniStats label="HITL résolus" value="89" delta={-3} deltaLabel="ce mois" icon="bi-person-check" variant="action" />
          <MiniStats label="Taux succès" value="94.2%" icon="bi-check-circle" variant="neutral" />
          <MiniStats label="Latence moy." value="1.8s" delta={8} deltaLabel="amélioration" icon="bi-speedometer2" variant="primary" />
        </div>
      </div>

      {/* Gauges */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Gauge — taux de complétion</h3>
        <div style={{ display: 'flex', gap: 'var(--hrl-space-8)', flexWrap: 'wrap', alignItems: 'center' }}>
          <Gauge value={42} label="HITL résolution" />
          <Gauge value={68} label="Jobs E-8 DS" />
          <Gauge value={94} label="Tests passants" />
        </div>
      </div>

      {/* LineChart */}
      <div className={styles.section}>
        <LineChart
          title="Exécutions jobs / mois"
          labels={MONTHS}
          datasets={[
            { label: 'Jobs auto', data: JOBS_DATA },
            { label: 'HITL requis', data: HITL_DATA, color: '#16a34a' },
          ]}
        />
      </div>

      {/* BarChart + DonutChart */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--hrl-space-4)' }}>
        <div className={styles.section}>
          <BarChart
            title="Jobs par service"
            labels={['Cardio', 'Neuro', 'Urgences', 'Chirurgie']}
            datasets={[{ label: 'Jobs', data: [45, 32, 28, 19] }]}
            height={200}
          />
        </div>
        <div className={styles.section}>
          <DonutChart
            title="Répartition types d'actes"
            labels={['Médicamenteux', 'DAR', 'Observation', 'Résumé', 'Alerte']}
            data={[38, 22, 18, 14, 8]}
            height={200}
          />
        </div>
      </div>
    </div>
  );
}

// ── Onglet Patterns ───────────────────────────────────────────────────────────

/** Carte d'un état du cercle : détail replié par défaut, déplié par le chevron de la pastille (VREC1 §3.5-3.6). */
function VoiceStateCard({ doc, size }: { doc: VoiceStateDoc; size: VoiceRecorderSize }) {
  const [open, setOpen] = useState(false);
  const detailsId = `ds-state-${doc.id}`;
  return (
    <article className={styles.stateCard}>
      <h4 className={styles.stateTitle}>{doc.titre}</h4>
      <div className={styles.stateVisual}>
        <VoiceRecorder size={size} state={doc.id} onToggle={() => {}} ariaLabel={`Exemple : ${doc.titre}`} />
        <VoiceStatusPill
          state={doc.id}
          live={false}
          expanded={open}
          detailsId={detailsId}
          onToggleDetails={() => setOpen((o) => !o)}
        />
      </div>
      <dl id={detailsId} className={styles.stateSpec} hidden={!open}>
        <dt>Déclencheur</dt>
        <dd>{doc.declencheur}</dd>
        <dt>Couleur</dt>
        <dd>{doc.couleur}</dd>
        <dt>Dégradé</dt>
        <dd>{doc.degrade}</dd>
        <dt>Halo</dt>
        <dd>{doc.halo}</dd>
        <dt>Barres</dt>
        <dd>{doc.barres}</dd>
        <dt>Animation</dt>
        <dd>{doc.animation}</dd>
        <dt>Texte</dt>
        <dd>{VOICE_LABEL[doc.id]}</dd>
      </dl>
    </article>
  );
}

function TabPatterns() {
  const [recSize, setRecSize] = useState<VoiceRecorderSize>('sm');
  const [vocal2State, setVocal2State] = useState<VoiceRecorderState>('off');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [hitlLoading, setHitlLoading] = useState(false);

  const handleHitl = () => {
    setHitlLoading(true);
    setTimeout(() => setHitlLoading(false), 1200);
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>PatientCard — PHI masqué</h3>
        <div style={{ display: 'flex', gap: 'var(--hrl-space-4)', flexWrap: 'wrap' }}>
          <div style={{ width: '220px' }}>
            <PatientCard displayName="Dupont J." service="Cardiologie" statut="actif" onView={() => {}} />
          </div>
          <div style={{ width: '220px' }}>
            <PatientCard displayName="Martin M." service="Neurologie" statut="urgence" />
          </div>
          <div style={{ width: '220px' }}>
            <PatientCard displayName="Bernard P." service="Urgences" statut="sorti" />
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>VoiceRecorder — états détaillés</h3>
        <p className={styles.sectionNote}>Les 7 états du cercle, une couleur, une icône et un texte chacun. Le chevron sous la pastille déplie le détail de la carte. Survolez ou tabulez sur une carte pour voir son animation.</p>
        <div className={styles.toggleRow}>
          {(['sm', 'lg'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={recSize === s ? styles.toggleActive : styles.toggle}
              aria-pressed={recSize === s}
              onClick={() => setRecSize(s)}
            >
              {s === 'sm' ? 'Petit (96 px)' : 'Grand (320 px)'}
            </button>
          ))}
        </div>
        <div className={recSize === 'lg' ? `${styles.stateGrid} ${styles.stateGridLg}` : styles.stateGrid}>
          {VOICE_STATE_DOCS.map((d) => (
            <VoiceStateCard key={d.id} doc={d} size={recSize} />
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Fenêtre vocale /vocal2</h3>
        <p className={styles.sectionNote}>La fenêtre flottante telle qu'elle se posera sur le DPI. Glissez la poignée, touchez-la pour changer de coin, ou utilisez Maj + flèches. Le chevron sous la pastille déplie ou replie le détail ; ouvert, il reste ouvert d'un état à l'autre.</p>
        <div className={styles.toggleRow}>
          {VOICE_STATE_DOCS.map((d) => (
            <button
              key={d.id}
              type="button"
              className={vocal2State === d.id ? styles.toggleActive : styles.toggle}
              aria-pressed={vocal2State === d.id}
              onClick={() => setVocal2State(d.id)}
            >
              {d.titre}
            </button>
          ))}
        </div>
        <div className={styles.vocalStage}>
          <p className={styles.vocalStageLabel}>DPI (page simulée)</p>
          <div className={styles.vocalStageContent}>
            <p className={styles.vocalStagePatient}>Lucien DÉMO · né le 03/02/1958 · Chambre 101 · IPP PAT-001</p>
            <table className="table table-sm">
              <thead>
                <tr><th>Heure</th><th>TA (mmHg)</th><th>FC (bpm)</th><th>SpO₂ (%)</th><th>T° (°C)</th></tr>
              </thead>
              <tbody>
                <tr><td>06:00</td><td>131/78</td><td>76</td><td>96</td><td>37,4</td></tr>
                <tr><td>10:00</td><td>126/74</td><td>71</td><td>97</td><td>37,1</td></tr>
                <tr><td>14:00</td><td>128/76</td><td>72</td><td>97</td><td>37,0</td></tr>
                <tr><td>18:00</td><td>124/72</td><td>70</td><td>98</td><td>36,9</td></tr>
              </tbody>
            </table>
          </div>
          <FloatingPanel storageKey="hrl-ds-vocal2-panel" glow={VOICE_COLOR[vocal2State]}>
            <div className={styles.vocalBody}>
              <span className={styles.vocalSoignant}>
                <strong>Docteur</strong> : Michel Machin
              </span>
              <span className={styles.vocalPatient}>
                <i className="bi bi-person-vcard" aria-hidden="true" /> Ch. 101 · Lucien DÉMO
              </span>
              <VoiceRecorder
                size="sm"
                state={vocal2State}
                onToggle={() => setVocal2State(vocal2State === 'off' ? 'listening' : 'off')}
                ariaLabel={vocal2State === 'off' ? 'Activer la saisie vocale' : 'Désactiver la saisie vocale'}
              />
              <VoiceStatusPill
                state={vocal2State}
                expanded={detailsOpen}
                detailsId="ds-vocal2-details"
                onToggleDetails={() => setDetailsOpen((o) => !o)}
              />
              <div id="ds-vocal2-details" className={styles.vocalDetails} hidden={!detailsOpen}>
                <p className={styles.vocalPrompt}>{vocal2State === 'off' ? 'Touchez le cercle pour activer la saisie vocale.' : 'Constantes pour Lucien DÉMO ?'}</p>
                <form className={styles.vocalForm} onSubmit={(e) => e.preventDefault()}>
                  <input className="form-control form-control-sm" aria-label="Écrire au lieu de parler" placeholder="Écrire au lieu de parler" />
                  <button type="submit" className="btn btn-sm btn-primary">Dire</button>
                </form>
              </div>
            </div>
          </FloatingPanel>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>HitlCard — approbation humaine</h3>
        <div style={{ maxWidth: '360px' }}>
          <HitlCard
            jobId="4a2f-bc91"
            agentName="AlertAgent"
            acteType="Traçabilité médicamenteuse"
            timestamp="14:23"
            onApprove={handleHitl}
            onReject={handleHitl}
            loading={hitlLoading}
          />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>EpicRow</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hrl-space-2)' }}>
          <EpicRow name="DS-HORALIIA" sprint="v0.6" done={48} total={64} tags={['DESIGN-SYSTEM']} />
          <EpicRow name="CORE-MATURE" sprint="v0.5" done={38} total={38} tags={['CORE']} />
          <EpicRow name="VISION-RPA" sprint="v1.0" done={19} total={19} tags={['VISION']} />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>StoryRow</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hrl-space-1)' }}>
          <StoryRow id="US-118" title="DS Fondations — tokens, son-core, page shell" status="done" points={8} assignee="Clé" />
          <StoryRow id="US-125" title="DS Atoms — 11 composants Button/Badge/Input..." status="done" points={13} />
          <StoryRow id="US-130" title="DS Layouts — 8 layouts" status="inbox" points={8} />
        </div>
      </div>
    </div>
  );
}

// ── Onglet Layouts ────────────────────────────────────────────────────────────

const TIMELINE_ITEMS: TimelineItem[] = [
  { id: '1', label: 'Admission', date: '08:00', content: 'Patient pris en charge aux urgences', variant: 'default' },
  { id: '2', label: 'Bilan', date: '09:30', content: 'Bilan biologique réalisé — résultats normaux', variant: 'success' },
  { id: '3', label: 'Alerte', date: '11:15', content: 'Fréquence cardiaque élevée — surveillance renforcée', variant: 'warning' },
  { id: '4', label: 'Sortie', date: '14:00', content: 'Patient transféré en service de cardiologie', variant: 'danger' },
];

function TabLayouts() {
  return (
    <div className={styles.tabContent}>

      {/* MainLayout */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>MainLayout</h3>
        <p className={styles.sectionDesc}>Header sticky + main scrollable + footer optionnel. Fond <code>--hrl-bg-gradient</code>.</p>
        <div style={{ border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-lg)', overflow: 'hidden', height: 180 }}>
          <MainLayout
            header={<div style={{ padding: '0.5rem 1rem', fontSize: 'var(--hrl-text-sm)', fontWeight: 600, color: 'var(--hrl-purple)' }}>Header glassmorphism</div>}
            footer={<span>Footer optionnel</span>}
          >
            <div style={{ padding: '0.5rem', fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-600)' }}>
              Contenu principal — slot children
            </div>
          </MainLayout>
        </div>
      </section>

      {/* AdminShellLayout */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>AdminShellLayout</h3>
        <p className={styles.sectionDesc}>Header + sidebar fixe + main. Pattern admin Horaliia.</p>
        <div style={{ border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-lg)', overflow: 'hidden', height: 160 }}>
          <AdminShellLayout
            header={<div style={{ padding: '0.5rem 1rem', fontSize: 'var(--hrl-text-sm)', fontWeight: 600 }}>Header admin</div>}
            sidebar={<div style={{ padding: '0.75rem', fontSize: 'var(--hrl-text-xs)', color: 'var(--hrl-neutral-500)' }}>Sidebar<br/>Nav items</div>}
            sidebarWidth="140px"
          >
            <div style={{ fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-600)' }}>Contenu admin</div>
          </AdminShellLayout>
        </div>
      </section>

      {/* AuthLayout */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>AuthLayout</h3>
        <p className={styles.sectionDesc}>Centrage vertical/horizontal, carte glassmorphism max 420px.</p>
        <div style={{ border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-lg)', overflow: 'hidden', height: 180 }}>
          <AuthLayout logo={<span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--hrl-purple)' }}>HORALIIA</span>}>
            <p style={{ textAlign: 'center', fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-600)' }}>Formulaire de connexion</p>
          </AuthLayout>
        </div>
      </section>

      {/* DashboardGrid */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>DashboardGrid</h3>
        <p className={styles.sectionDesc}>Grille CSS responsive configurable (1→4 colonnes, gap sm/md/lg).</p>
        <DashboardGrid columns={3} gap="md">
          {['KPI 1', 'KPI 2', 'KPI 3'].map(k => (
            <div key={k} style={{ background: 'var(--hrl-glass-bg)', border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-md)', padding: 'var(--hrl-space-4)', textAlign: 'center', fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-700)' }}>
              {k}
            </div>
          ))}
        </DashboardGrid>
      </section>

      {/* TwoColumnLayout */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>TwoColumnLayout</h3>
        <p className={styles.sectionDesc}>Main + aside (gauche ou droite), responsive, largeur configurable.</p>
        <TwoColumnLayout
          asideWidth="180px"
          main={<div style={{ background: 'var(--hrl-glass-bg)', border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-md)', padding: 'var(--hrl-space-4)', fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-700)' }}>Contenu principal</div>}
          aside={<div style={{ background: 'rgba(147,51,234,0.06)', border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-md)', padding: 'var(--hrl-space-4)', fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-600)' }}>Aside (right)</div>}
        />
      </section>

      {/* FullPageCenter */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>FullPageCenter</h3>
        <p className={styles.sectionDesc}>Centrage pleine page — pages vides, loaders, erreurs 404.</p>
        <div style={{ border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-lg)', overflow: 'hidden', height: 140 }}>
          <FullPageCenter withBackground>
            <i className="bi bi-exclamation-circle" style={{ fontSize: '2rem', color: 'var(--hrl-purple)' }}></i>
            <p style={{ fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-600)' }}>Contenu centré — ex: erreur 404</p>
          </FullPageCenter>
        </div>
      </section>

      {/* CardGrid */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>CardGrid</h3>
        <p className={styles.sectionDesc}>CSS Grid auto-fill responsive — largeur minimale configurable.</p>
        <CardGrid minCardWidth="160px" gap="sm">
          {['Card A', 'Card B', 'Card C', 'Card D'].map(c => (
            <div key={c} style={{ background: 'var(--hrl-glass-bg)', border: '1px solid var(--hrl-glass-border)', borderRadius: 'var(--hrl-radius-md)', padding: 'var(--hrl-space-3)', textAlign: 'center', fontSize: 'var(--hrl-text-sm)', color: 'var(--hrl-neutral-700)' }}>
              {c}
            </div>
          ))}
        </CardGrid>
      </section>

      {/* TimelineLayout */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>TimelineLayout</h3>
        <p className={styles.sectionDesc}>Historique chronologique avec marqueurs colorés — traces AI Act, historique patient.</p>
        <TimelineLayout items={TIMELINE_ITEMS} />
      </section>

    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export function DesignSystemPage() {
  const [activeTab, setActiveTab] = useState<TabId>('fondations');

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>
          <i className="bi bi-palette me-2" style={{ color: 'var(--hrl-purple)' }}></i>
          Design System Horaliia
          <span className="badge ms-3" style={{ background: 'var(--hrl-purple-soft)', color: 'var(--hrl-purple)', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '1px', verticalAlign: 'middle' }}>
            HRL-DS v0.6
          </span>
        </h1>
        <p className={styles.subtitle}>
          7 onglets · 53 composants · CSS Modules · Tokens <code>--hrl-*</code> · Protocole SON
        </p>
      </div>

      {/* Nav onglets */}
      <nav className={styles.tabNav}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabBtnActive : ''} ${!tab.available ? styles.tabBtnDisabled : ''}`}
            onClick={() => tab.available && setActiveTab(tab.id)}
            disabled={!tab.available}
          >
            <i className={`bi ${tab.icon} me-1`}></i>
            {tab.label}
            {!tab.available && (
              <span className={styles.tabBtnBadge}>Soon</span>
            )}
          </button>
        ))}
      </nav>

      {/* Contenu onglets */}
      {activeTab === 'fondations' && <TabFondations />}
      {activeTab === 'atoms'      && <TabAtoms />}
      {activeTab === 'molecules'  && <TabMolecules />}
      {activeTab === 'organisms'  && <TabOrganisms />}
      {activeTab === 'charts'     && <TabCharts />}
      {activeTab === 'patterns'   && <TabPatterns />}
      {activeTab === 'layouts'    && <TabLayouts />}
    </div>
  );
}
